import LanguageSelector from './LanguageSelector'
import { Link } from '../i18n/LocalizedLink'
import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useInquiry } from './InquirySheet'
import './Navbar.css'

const BANNER_H = 52
const NAV_GAP = 12

interface NavbarProps {
  /** When true, disables the dark-overlay hero mode and banner offset */
  lightMode?: boolean
  /** CSS selector for the hero element to track. Defaults to '.hero'. Only used when lightMode is false. */
  heroSelector?: string
  /** Set true only when an AnnouncementBanner is present above the navbar */
  hasBanner?: boolean
}

export default function Navbar({ lightMode = false, heroSelector = '.hero', hasBanner = false }: NavbarProps) {
  const { t } = useLocale()
  const { openInquiry } = useInquiry()
  const wrapRef     = useRef<HTMLElement>(null)
  const logoRef     = useRef<HTMLImageElement>(null)
  const linksRef    = useRef<HTMLDivElement>(null)
  const pillRef     = useRef<HTMLSpanElement>(null)
  const [scrolled,  setScrolled]  = useState(false)
  const [overHero,  setOverHero]  = useState(!lightMode)
  const [menuOpen,  setMenuOpen]  = useState(false)
  const location = useLocation()

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  // Keep the logo in sync with the navbar's light/dark surface.
  useEffect(() => {
    if (!logoRef.current || lightMode) return
    logoRef.current.src = overHero ? '/lockup_web_dark.png' : '/lockup_web_light.png'
  }, [overHero, lightMode])

  useEffect(() => {
    const OFFSET = hasBanner ? BANNER_H : 0
    const hero = lightMode ? null : document.querySelector<HTMLElement>(heroSelector)

    const onScroll = () => {
      const sy = window.scrollY

      if (wrapRef.current) {
        wrapRef.current.style.top = `${Math.max(NAV_GAP, OFFSET + NAV_GAP - sy)}px`
      }

      setScrolled(sy > 20)

      if (!lightMode) {
        const isOverHero = hero ? hero.getBoundingClientRect().bottom > 64 : false
        setOverHero(isOverHero)
        if (logoRef.current) {
          logoRef.current.src = isOverHero ? '/lockup_web_dark.png' : '/lockup_web_light.png'
        }
      }
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [lightMode, heroSelector, hasBanner])

  const classes = [
    'navbar-wrap',
    scrolled  && 'scrolled',
    overHero  && 'over-hero',
    menuOpen  && 'menu-open',
  ].filter(Boolean).join(' ')

  const isActive = (path: string) => {
    const active = path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(`${path}/`)
    return active ? 'active' : undefined
  }

  const close = () => setMenuOpen(false)

  // Sliding pill handlers — direct DOM for zero-jank animation
  const onLinkEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const pill      = pillRef.current
    const container = linksRef.current
    if (!pill || !container) return
    const cr = container.getBoundingClientRect()
    const lr = e.currentTarget.getBoundingClientRect()
    pill.style.left    = `${lr.left - cr.left}px`
    pill.style.width   = `${lr.width}px`
    pill.style.opacity = '1'
  }

  const onLinksLeave = () => {
    if (pillRef.current) pillRef.current.style.opacity = '0'
  }

  return (
    <header ref={wrapRef} className={classes}>
      <nav className="navbar">

        {/* ── Top bar: always visible ── */}
        <div className="navbar-top">
          <Link to="/" className="navbar-logo" aria-label="Bybeit" onClick={close}>
            <img
              ref={logoRef}
              src={lightMode ? '/lockup_web_light.png' : '/lockup_web_dark.png'}
              alt="Bybeit"
            />
          </Link>

          <div className="navbar-links" ref={linksRef} onMouseLeave={onLinksLeave}>
            <span className="navbar-pill" ref={pillRef} aria-hidden="true" />
            <Link to="/" data-active={isActive('/')} onMouseEnter={onLinkEnter}>Home</Link>
            <Link to="/about" data-active={isActive('/about')} onMouseEnter={onLinkEnter}>About</Link>
            <Link to="/blog" data-active={isActive('/blog')} onMouseEnter={onLinkEnter}>Blog</Link>
            <Link to="/newsroom" data-active={isActive('/newsroom')} onMouseEnter={onLinkEnter}>Newsroom</Link>
            <Link to="/company" data-active={isActive('/company')} onMouseEnter={onLinkEnter}>Company</Link>
          </div>

          <LanguageSelector />

          <button type="button" className="navbar-cta" onClick={() => openInquiry()}>Talk to Sales</button>

          <button
            className="navbar-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? t("Close menu") : t("Open menu")}
            aria-expanded={menuOpen}
          >
            <span className="hamburger-lines">
              <span />
              <span />
            </span>
          </button>
        </div>

        {/* ── Mobile drawer (morphs open below top bar) ── */}
        <div className={`navbar-menu${menuOpen ? ' is-open' : ''}`} aria-hidden={!menuOpen}>
          <div className="navbar-menu-links">
            <Link to="/" onClick={close}>Home</Link>
            <Link to="/about" onClick={close}>About</Link>
            <Link to="/blog" onClick={close}>Blog</Link>
            <Link to="/newsroom" onClick={close}>Newsroom</Link>
            <Link to="/company" onClick={close}>Company</Link>
            <button
              type="button"
              className="navbar-menu-cta"
              onClick={() => {
                close()
                openInquiry()
              }}
            > Talk to Sales </button>
          </div>
        </div>

      </nav>
    </header>
  )
}
